import React, { useState, useRef, useEffect } from "react";
import { string, object, func } from "prop-types";
import { useDispatch } from "react-redux";
import get from "lodash/get";
import { updateUserProfile } from "@quintype/bridgekeeper-js";

import CameraIcon from "../../../../assets/icons/camera";
import EditIcon from "../../../../assets/icons/edit";
import { ProfileImageUploadModal } from "../profile-image-upload-modal";
import { ProfileImageDropdown } from "../profile-image-dropdown";
import { MEMBER_UPDATED } from "../../store/actions";
import "./profile-card.m.css";

export const ProfileCard = ({
  profileData,
  onEditClick,
  onImageUpload,
  onImageModalStateChange,
  className = "",
  ...props
}) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dispatch = useDispatch();

  const {
    name = "",
    title = "",
    membership = "",
    email = "",
    phone = "",
    city = "",
    profileImage = null,
    designation = ""
  } = profileData || {};

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const handleImageUpload = async (uploadData) => {
    if (!uploadData) {
      // Handle remove case
      if (onImageUpload) {
        onImageUpload(null);
      }
      return;
    }

    const { tempImageKey } = uploadData;

    try {
      // Prepare body following EditProfile.js pattern
      const body = {
        name: name,
        email: email || profileData["phone-number"]
      };
      body["temp-s3-key"] = tempImageKey;

      // Update user profile with the temp S3 key
      const currentUserResp = await updateUserProfile(body);

      // Update Redux store with the response data following EditProfile.js pattern
      const updatedUser = get(currentUserResp, ["user"], null);
      if (updatedUser) {
        dispatch({
          type: MEMBER_UPDATED,
          member: updatedUser
        });
      }

      if (onImageUpload) {
        const avatarUrl = get(updatedUser, ['avatar-url'], null);
        onImageUpload({
          ...uploadData,
          avatarUrl,
          user: updatedUser
        });
      }
    } catch (error) {
      console.error("Error uploading profile image:", error);
    }
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
    setIsDropdownOpen(false);
    if (onImageModalStateChange) {
      onImageModalStateChange(true);
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    if (onImageModalStateChange) {
      onImageModalStateChange(false);
    }
  };

  const toggleDropdown = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isMobile) {
      // On mobile, directly open the upload modal instead of showing dropdown
      openImageModal();
    } else {
      // On desktop/tablet, show the dropdown
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleUploadClick = () => {
    openImageModal();
  };

  const handleRemoveClick = async () => {
    try {
      // Prepare body following EditProfile.js pattern
      const body = {
        name: name,
        email: email || profileData["phone-number"],
        'avatar-url': null,
        'avatar-s3-key': null
      };

      // Call updateUserProfile to remove the image
      const currentUserResp = await updateUserProfile(body);

      // Update Redux store with the response data following EditProfile.js pattern
      const updatedUser = get(currentUserResp, ["user"], null);
      if (updatedUser) {
        dispatch({
          type: MEMBER_UPDATED,
          member: updatedUser
        });
      }

      // Call the onImageUpload callback with null avatar URL to remove the image
      if (onImageUpload) {
        onImageUpload({
          avatarUrl: null,
          user: updatedUser,
          removed: true
        });
      }
    } catch (error) {
      console.error("Error removing profile image:", error);
    }

    closeDropdown();
  };

  return (
    <div styleName="profile-card" className={className} {...props}>
      <div styleName="profile-wrapper">
        <div styleName="profile-image-container">
          <div styleName={`profile-image ${profileImage ? 'has-image' : 'has-placeholder'}`}>
            {profileImage ? (
              <img src={profileImage} alt={name} styleName="image" />
            ) : (
                <div styleName="placeholder-image">
                  <img
                    src="/icons/Group 333.png"
                    alt="Profile placeholder"
                    styleName="placeholder-img"
                  />
                </div>
            )}
            <div styleName="camera-button-wrapper">
              <button
                styleName="camera-button"
                type="button"
                onClick={toggleDropdown}
              >
                <CameraIcon color="white" width="20" height="20" />
              </button>

              {!isMobile && (
                <ProfileImageDropdown
                  isOpen={isDropdownOpen}
                  onClose={closeDropdown}
                  onUploadClick={handleUploadClick}
                  onRemoveClick={handleRemoveClick}
                  hasImage={!!profileImage}
                />
              )}
            </div>
          </div>
        </div>

        <div styleName="profile-info">
          <div styleName="header">
            <h1 styleName="main-heading">{name}</h1>
            {designation && <p styleName="designation-sub-header">{designation}</p>}
          </div>

          <div styleName="details">
            <div styleName="name-section">
              <h2 styleName="member-title">{title}</h2>
              <p styleName="membership-info">{membership}</p>
            </div>

            <div styleName="contact-info">
              <div styleName="metadata">
                <div styleName="info-item">
                  <span styleName="info-text">{email}</span>
                </div>
                <div styleName="info-item">
                  <span styleName="info-text">{phone}</span>
                </div>
                <div styleName="info-item">
                  <span styleName="info-text">{city}</span>
                </div>
              </div>

              <button
                styleName="edit-button"
                type="button"
                onClick={onEditClick}
              >
                <EditIcon color="#FF2626" />
                <span styleName="edit-text">Request profile update</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <ProfileImageUploadModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        onImageUpload={handleImageUpload}
        hasImage={!!profileImage}
        onRemoveClick={handleRemoveClick}
      />
    </div>
  );
};

ProfileCard.propTypes = {
  profileData: object.isRequired,
  onEditClick: func,
  onImageUpload: func,
  onImageModalStateChange: func,
  className: string
};